import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
  type RefObject,
} from 'react';
import { ArrowDown } from 'lucide-react';
import { AnimatePresence, motion, type HTMLMotionProps } from 'motion/react';
import { cn } from '@/utils/cn';
import { useAutoScroll } from '@/embedded/chat/hooks/use-auto-scroll';

type ScrollToBottomContextValue = {
  bottomRef: RefObject<HTMLDivElement | null> | null;
  showTrigger: boolean;
  setShowTrigger: (show: boolean) => void;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  observeBottomAnchor: (element: Element | null) => void;
};

const ScrollToBottomContext = createContext<ScrollToBottomContextValue>({
  bottomRef: null,
  showTrigger: false,
  setShowTrigger: () => {},
  scrollToBottom: () => {},
  observeBottomAnchor: () => {},
});

export const ScrollToBottomProvider = ({
  children,
  containerRef,
  enableAutoScroll = true,
}: {
  children: ReactNode;
  containerRef?: RefObject<HTMLElement | null>;
  enableAutoScroll?: boolean;
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const [showTrigger, setShowTrigger] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (containerRef?.current) {
      scrollContainerRef.current = containerRef.current;
      return;
    }

    const maskedScrollArea = document.querySelector(
      '.masked-scroll-area'
    ) as HTMLElement | null;
    if (maskedScrollArea) {
      scrollContainerRef.current = maskedScrollArea;
    }
  }, [containerRef]);

  const { scrollToBottom, observeBottomAnchor } = useAutoScroll(
    scrollContainerRef,
    enableAutoScroll
  );

  return (
    <ScrollToBottomContext.Provider
      value={useMemo(
        () => ({
          bottomRef,
          showTrigger,
          setShowTrigger,
          scrollToBottom,
          observeBottomAnchor,
        }),
        [
          bottomRef,
          observeBottomAnchor,
          scrollToBottom,
          setShowTrigger,
          showTrigger,
        ]
      )}
    >
      {children}
    </ScrollToBottomContext.Provider>
  );
};

export const ScrollToBottomTrigger = ({
  className,
  onClick,
  ...attrs
}: HTMLMotionProps<'button'>) => {
  const { showTrigger, scrollToBottom } = useContext(ScrollToBottomContext);

  return (
    <AnimatePresence>
      {showTrigger ? (
        <motion.button
          key="scroll-to-bottom-trigger"
          type="button"
          initial={{ opacity: 0, y: 10, scale: 0.8 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className={cn(
            'flex size-9 items-center justify-center rounded-full border border-border/70 bg-background/90 text-muted-foreground shadow-lg backdrop-blur transition-colors hover:text-foreground',
            className
          )}
          onClick={event => {
            onClick?.(event);
            scrollToBottom('smooth');
          }}
          {...attrs}
        >
          <ArrowDown className="size-4" />
        </motion.button>
      ) : null}
    </AnimatePresence>
  );
};

const SHOW_DELAY_MS = 300;

export const ScrollToBottomAnchor = () => {
  const { bottomRef, setShowTrigger, observeBottomAnchor } = useContext(
    ScrollToBottomContext
  );
  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const element = bottomRef?.current;
    if (!element) {
      return;
    }

    observeBottomAnchor(element);

    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) {
          if (!showTimeoutRef.current) {
            showTimeoutRef.current = setTimeout(() => {
              setShowTrigger(true);
              showTimeoutRef.current = null;
            }, SHOW_DELAY_MS);
          }
          return;
        }

        if (showTimeoutRef.current) {
          clearTimeout(showTimeoutRef.current);
          showTimeoutRef.current = null;
        }
        setShowTrigger(false);
      });
    });

    observer.observe(element);

    return () => {
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
        showTimeoutRef.current = null;
      }
      observer.disconnect();
      observeBottomAnchor(null);
      setShowTrigger(false);
    };
  }, [bottomRef, observeBottomAnchor, setShowTrigger]);

  return <div className="w-full" ref={bottomRef as Ref<HTMLDivElement>} />;
};
